import { useEffect, useState } from "react";
import useArrayObject from "../../hooks/useArrayObject";
import useInputObject from "../../hooks/useInputObject";
import {
  getChainStoreBySlipNo,
  getChainStoreOrder,
  getProductMapping,
  upsertChainStore,
} from "../../http/api/ChainStoreAPI";
import { getHyphenedDate } from "../../utils/utils";
import { isNotNull, isNullEmpty } from "../../utils/validation";
import { PRICE_DETAIL_TYPE } from "../../common/const";

const FIXED_DETAIL_ROW_COUNT = 12;

const initialHeader = {
  slipGroupId: null,
  slipNo: null,
  shipmentDate: getHyphenedDate(),
  buyerNo: null,
  classNo: null,
  division: null,
  priceDetailTypeId: 0,
};

const initialDetail = {
  slipGroupId: null,
  slipDetailId: null,
  slipNo: null,
  productId: null,
  productDetailId: null,
  productName: "",
  chainStoreProductName: "",
  originId: null,
  rankId: null,
  productClassId: null,
  packingId: null,
  unitQuantity: null,
  quantity: 0,
  unitPrice: 0,
};

const initialDetails = (startId) =>
  [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((index) => ({
    ...initialDetail,
    slipDetailId: index + startId,
  }));

function fillDetails(listOrder) {
  const result = [...listOrder];
  for (let i = listOrder.length; i < FIXED_DETAIL_ROW_COUNT; i++) {
    result.push({ ...initialDetail, index: i, slipDetailId: i + 1 });
  }
  return result;
}

const useChainStoreAPI = () => {
  const header = useInputObject(initialHeader);
  const details = useArrayObject(initialDetails(1));

  function setupNewInvoice() {
    header.setValue(initialHeader);
    details.setValue(initialDetails(1));
  }

  useEffect(() => {
    //注文データ取得時でない場合は終了
    if (
      !header.Value.priceDetailTypeId ||
      header.Value.priceDetailTypeId === PRICE_DETAIL_TYPE.NONE
    ) {
      return;
    }
    //注文データクリアの場合は一覧をクリア
    if (header.Value.priceDetailTypeId === PRICE_DETAIL_TYPE.CLEAR) {
      clearDetails();
      return;
    }

    //伝票番号が入力されている場合、伝票番号を元にして取得した明細データを優先し、
    //注文データからは取得しない
    if (!isNullEmpty(header.Value.slipNo)) {
      alert("伝票番号が入力されているため、注文データは取得できません。");
      return;
    }
    // 日付、取引先が両方指定されている場合以外は注文データ取得不可
    if (
      isNullEmpty(header.Value.shipmentDate) ||
      isNullEmpty(header.Value.buyerNo)
    ) {
      return;
    }

    getOrderData();
  }, [header.Value.priceDetailTypeId]);

  useEffect(() => {
    if (isNullEmpty(header.Value.slipNo)) return;
    getDataBySlipNo();
  }, [header.Value.slipNo]);

  async function getDataBySlipNo() {
    try {
      const response = await getChainStoreBySlipNo(header.Value.slipNo);
      const chainStoreInfo = response.listChainStoreInfo[0];
      let listDetail = chainStoreInfo.listDetail.map((p, i) => ({
        ...p,
        index: i,
        slipDetailId: i + 1,
      }));
      listDetail = fillDetails(listDetail);
      header.setValue({
        ...chainStoreInfo.groupInfo,
        slipNo: header.Value.slipNo,
        shipmentDate: getHyphenedDate(
          new Date(chainStoreInfo.groupInfo.shipmentDate)
        ),
      });
      details.setValue(listDetail);
    } catch (e) {
      console.log("Error when get data by slipNo");
      header.setValue({ ...initialHeader, slipNo: header.Value.slipNo });
      details.setValue(fillDetails([]));
    }
  }

  async function getOrderData() {
    try {
      let response = await getChainStoreOrder(
        header.Value.shipmentDate,
        header.Value.buyerNo,
        header.Value.priceDetailTypeId
      );
      let listOrder = response.map((p, i) => ({
        ...p,
        index: i,
        slipDetailId: i + 1,
      }));
      listOrder = fillDetails(listOrder);
      details.setValue(listOrder);
      header.update("priceDetailTypeId", 0);
      // return listOrder;
    } catch (e) {
      console.log("Error when get order data");
      details.setValue(fillDetails([]));
      header.update("priceDetailTypeId", 0);
      // return fillDetails([]);
    }
  }

  async function clearDetails() {
    details.setValue(fillDetails([]));
  }

  // データをアップサートする非同期関数
  async function upsert(data) {
    try {
      // 請求書データをフィルタリング
      const filteredData = filterInvoice(data);

      // フィルタリングされたデータを検証
      const err = await verifyData(filteredData);

      // 検証エラーがある場合、アラートを表示して処理を終了
      if (!isNullEmpty(err)) {
        alert(err);
        return; // エラー時に処理を中断
      }

      // アップサート処理を実行 (実際のアップサート呼び出しはコメントアウト)
      await upsertChainStore(filteredData);
      
      // 必要に応じて新しい請求書のセットアップを実行
      setupNewInvoice();

      // 成功メッセージを表示
      // alert("登録完了しました。");
    } catch (error) {
      // アップサート処理中の予期しないエラーを処理
      console.error("アップサート中のエラー:", error);
      alert("登録できませんでした。");
    }
  }

  // データ検証関数 
  async function verifyData(data) {
    // classNoがnullまたは空かをチェック
    if (isNullEmpty(data?.groupInfo?.classNo)) return "分類Noは必須です。";

    // listDetailが空の場合をチェック
    if (data.listDetail.length === 0) return "明細を入力してください。";

    // 商品コードと分類Noの検証を実行
    const notValidProduct = await verifyProductMappingCode(data);

    // 検証結果に不一致の商品がある場合、エラーメッセージを作成して返す
    if (notValidProduct && notValidProduct.length > 0) {
      const productDetails = notValidProduct
        .map((item) => `${item.productId} - ${item.productName}`)
        .join("\n");
      return `選択した商品と分類Noが合わない商品があります。ご確認ください:\n${productDetails}`;
    }

    // 検証が成功した場合、空文字列を返す
    return "";
  }

  // 商品コードのマッピングを検証する関数
  async function verifyProductMappingCode(data) {
    // groupInfoからbuyerNoとclassNoを取得
    const buyerNo = data.groupInfo.buyerNo;
    const classNo = data.groupInfo.classNo;

    // buyerNoに基づいて商品マッピングを取得
    const listProductMapping = await getProductMapping(buyerNo);

    // 不一致の商品を格納する配列
    const mismatchedProducts = [];

    // listDetail内の各商品を検証
    data.listDetail.forEach((detail) => {
      // listProductMapping内でproductIdが一致する商品を検索
      const matchingProduct = listProductMapping.listChainStoreProductInfo.find(
        (x) => x.productId === detail.productId
      );

      // 商品が存在し、かつchainStoreProductTypeCdがclassNoと一致しない場合
      if (
        matchingProduct &&
        matchingProduct.chainStoreProductTypeCd != classNo
      ) {
        // 不一致の商品情報を結果配列に追加
        mismatchedProducts.push({
          productId: detail.productId,
          productName: detail.productName,
        });
      }
    });

    // 不一致の商品リストを返す。全て一致する場合はnullを返す
    return mismatchedProducts.length > 0 ? mismatchedProducts : null;
  }

  function filterInvoice(invoice) {
    let result = {};
    result.groupInfo = invoice.groupInfo;
    result.listDetail = invoice.listDetail.filter((detail) => {
      return isNotNull(detail.productId);
    });
    return result;
  }

  return {
    header,
    details,
    setupNewInvoice,
    initialDetail,
    upsert
  };
};

export default useChainStoreAPI;
